/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { MutableRef } from 'preact/hooks';
import { SampledWidths, TableColumn } from '../Table';
import { tableStyles } from '../themes/TableStyles.css';
import { ColumnWidths } from '../../UNSAFE_TableView';

/**
 * Helper method to extract the individual column widths of the Table
 * depending on its layout. With a 'fixed' layout, an initial 'availableWidth'
 * is required, while with a 'contents' layout, -1 can be provided as the
 * 'availableWidth' initially. This will trigger the layout logic to ensure
 * a non-contstrained Table width is supported, and is needed to match
 * current Redwood behaviors with a 'contents' layout.
 */
export function getLayoutColumnWidths<K, D, C extends string>(
  outerTableElement: HTMLDivElement,
  tableId: string,
  isFixed: boolean,
  sampledColumnWidthsRef: MutableRef<SampledWidths<C> | undefined>,
  availableWidth: number,
  columnsArray: TableColumn<K, D, C>[],
  staticColumnWidths?: ColumnWidths<C>
) {
  if (isFixed) {
    return _getFixedLayoutColumnWidths(availableWidth, columnsArray, staticColumnWidths);
  }
  let newColumnWidths = _getContentsLayoutColumnWidths(
    outerTableElement,
    tableId,
    sampledColumnWidthsRef,
    availableWidth,
    columnsArray,
    staticColumnWidths
  );
  if (availableWidth === -1) {
    let totalWidth = 0;
    const widthsArray = [];
    for (let i = 0; i < columnsArray.length; i++) {
      const width = newColumnWidths[columnsArray[i].key];
      widthsArray.push(width);
      totalWidth += width;
    }
    // for content sizing, apply the new column widths immediately, and check to see
    // if all space is actually utilized - this supports no width constraint scenarios
    _udpateTableSizing(outerTableElement, tableId, widthsArray, totalWidth);
    availableWidth = outerTableElement.scrollWidth;
    if (Math.abs(totalWidth - availableWidth) > 1) {
      newColumnWidths = _getContentsLayoutColumnWidths(
        outerTableElement,
        tableId,
        sampledColumnWidthsRef,
        availableWidth,
        columnsArray,
        staticColumnWidths
      );
    }
  }
  return newColumnWidths;
}

/**
 * Helper method to extract the individual column widths of the Table
 * for a 'fixed' layout table that already has its root width applied.
 */
function _getFixedLayoutColumnWidths<K, D, C extends string>(
  availableWidth: number,
  columnsArray: TableColumn<K, D, C>[],
  staticColumnWidths?: ColumnWidths<C>
) {
  const DEFAULT_COLUMN_WEIGHT = 1;
  const DEFAULT_COLUMN_MIN_WIDTH = 100;
  const DEFAULT_COLUMN_MAX_WIDTH = undefined;

  let requiresActiveSizing = false;
  let totalWorkingWeight = 0;
  let forcedTotalWidth = 0;
  let pendingTotalWidth = 0;
  const columnMaxWidthsArray = [];
  const columnWeightsArray = [];
  const forcedColumnWidthsArray = [];
  const newColumnWidthsArray = [];
  const appliedColumnWidthsArray = [];
  for (let i = 0; i < columnsArray.length; i++) {
    const column = columnsArray[i];
    let columnMinWidth = column.value.minWidth;
    if (columnMinWidth == null || columnMinWidth <= 0) {
      columnMinWidth = DEFAULT_COLUMN_MIN_WIDTH;
    }
    let columnMaxWidth = column.value.maxWidth;
    if (columnMaxWidth == null || columnMaxWidth < columnMinWidth) {
      columnMaxWidth = DEFAULT_COLUMN_MAX_WIDTH;
    }
    columnMaxWidthsArray.push(columnMaxWidth);
    let columnWeight = column.value.weight;
    if (columnWeight == null || columnWeight < 1) {
      columnWeight = DEFAULT_COLUMN_WEIGHT;
    }
    columnWeightsArray.push(columnWeight);
    const columnWidth = staticColumnWidths != null ? staticColumnWidths[column.key] : undefined;
    if (columnWidth != null) {
      forcedTotalWidth += columnWidth;
      pendingTotalWidth += columnWidth;
      forcedColumnWidthsArray.push(columnWidth);
      newColumnWidthsArray.push(columnWidth);
      appliedColumnWidthsArray.push(columnWidth);
    } else {
      requiresActiveSizing = true;
      pendingTotalWidth += columnMinWidth;
      totalWorkingWeight += columnWeight;
      forcedColumnWidthsArray.push(undefined);
      newColumnWidthsArray.push(columnMinWidth);
      appliedColumnWidthsArray.push(columnMinWidth);
    }
  }

  // enforce min and max rules if active sizing is required and available space remains
  if (requiresActiveSizing && pendingTotalWidth < availableWidth) {
    _enforceFixedMinMaxRules(
      columnsArray,
      availableWidth,
      totalWorkingWeight,
      forcedTotalWidth,
      forcedColumnWidthsArray,
      columnWeightsArray,
      columnMaxWidthsArray,
      newColumnWidthsArray,
      appliedColumnWidthsArray
    );
  }
  return _createColumnWidths(columnsArray, newColumnWidthsArray);
}

/**
 * Helper function to enforce all of the layout sizing rules on the Table's columns.
 */
function _enforceFixedMinMaxRules<K, D, C>(
  columnsArray: TableColumn<K, D, C>[],
  availableWidth: number,
  totalWorkingWeight: number,
  forcedTotalWidth: number,
  forcedColumnWidthsArray: (number | undefined)[],
  columnWeightsArray: number[],
  columnMaxWidthsArray: (number | undefined)[],
  newColumnWidthsArray: number[],
  appliedColumnWidthsArray: number[]
) {
  // try and honor the weights of each column combined with the min widths, and then check that against
  // any max widths once completed. if the final result doesn't fill the space AND at least 1 max width
  // rule was enforced, repeat the process until no max rules are enforced OR the space is filled.
  let isMaxRuleApplied = true;
  while (isMaxRuleApplied) {
    isMaxRuleApplied = false;
    _enforceWeightedMinRules(
      columnsArray,
      availableWidth,
      totalWorkingWeight,
      forcedTotalWidth,
      forcedColumnWidthsArray,
      columnWeightsArray,
      newColumnWidthsArray,
      appliedColumnWidthsArray
    );
    let pendingTotalWidth = 0;
    for (let i = 0; i < columnsArray.length; i++) {
      if (forcedColumnWidthsArray[i] == null) {
        const maxWidth = columnMaxWidthsArray[i];
        if (maxWidth != null && newColumnWidthsArray[i] > maxWidth) {
          isMaxRuleApplied = true;
          forcedColumnWidthsArray[i] = maxWidth;
          forcedTotalWidth += maxWidth;
          totalWorkingWeight -= columnWeightsArray[i];
          newColumnWidthsArray[i] = maxWidth;
          appliedColumnWidthsArray[i] = maxWidth;
        }
      }
      pendingTotalWidth += newColumnWidthsArray[i];
    }
    isMaxRuleApplied = isMaxRuleApplied && pendingTotalWidth < availableWidth;
  }
}

/**
 * Helper method to extract the individual column widths of the Table
 * for a 'contents' layout table that already has its root width applied.
 */
function _getContentsLayoutColumnWidths<K, D, C extends string>(
  outerTableElement: HTMLDivElement,
  tableId: string,
  sampledColumnWidthsRef: MutableRef<SampledWidths<C> | undefined>,
  availableWidth: number,
  columnsArray: TableColumn<K, D, C>[],
  staticColumnWidths?: ColumnWidths<C>
) {
  const DEFAULT_COLUMN_MIN_WIDTH = undefined;
  const DEFAULT_COLUMN_MAX_WIDTH = undefined;

  let requiresActiveSizing = false;
  const columnMinWidthsArray = [] as (number | undefined)[];
  const columnMaxWidthsArray = [];
  const forcedColumnWidthsArray = [];
  const newColumnWidthsArray = [];
  for (let i = 0; i < columnsArray.length; i++) {
    const column = columnsArray[i];
    let columnMinWidth = column.value.minWidth;
    if (columnMinWidth == null || columnMinWidth <= 0) {
      columnMinWidth = DEFAULT_COLUMN_MIN_WIDTH;
    }
    columnMinWidthsArray.push(columnMinWidth);
    let columnMaxWidth = column.value.maxWidth;
    if (columnMaxWidth == null || columnMaxWidth < (columnMinWidth != null ? columnMinWidth : 0)) {
      columnMaxWidth = DEFAULT_COLUMN_MAX_WIDTH;
    }
    columnMaxWidthsArray.push(columnMaxWidth);
    const columnWidth = staticColumnWidths != null ? staticColumnWidths[column.key] : undefined;
    if (columnWidth != null) {
      forcedColumnWidthsArray.push(columnWidth);
      newColumnWidthsArray[i] = columnWidth;
    } else {
      requiresActiveSizing = true;
      forcedColumnWidthsArray.push(undefined);
    }
  }

  // short-circuit normal contents sizing if each column specifies a 'width' value
  if (requiresActiveSizing) {
    // if active sizing is required, update min width values to reflect actual widths
    // performance note - this should be the only logic that requires DOM measurements
    let forcedTotalWidth = 0;
    let pendingTotalWidth = 0;
    let totalPreferredWidth = 0;
    let totalWorkingWeight = 0;
    let totalRemainingWeight = 0;
    const sampledWidths = sampledColumnWidthsRef.current;
    const sampledMinWidthsArray = [];
    const sampledWeightWidthsArray = [] as number[];
    const appliedColumnWidthsArray = [];
    if (sampledWidths != null) {
      for (let i = 0; i < columnsArray.length; i++) {
        sampledMinWidthsArray.push(sampledWidths.minWidths[columnsArray[i].key]);
        sampledWeightWidthsArray.push(sampledWidths.weightWidths[columnsArray[i].key]);
      }
    } else {
      sampledColumnWidthsRef.current = {
        minWidths: {} as ColumnWidths<C>,
        weightWidths: {} as ColumnWidths<C>
      };
      const headerElements = outerTableElement.querySelectorAll(
        `[data-oj-table-header-cell='${tableId}']`
      );
      // when sampledColumnWidths are not populated, content wrapping is disabled in the table
      // which allows for measurements to represent the column weights
      for (let i = 0; i < headerElements.length; i++) {
        const sampledWeightWidth = (headerElements[i] as HTMLElement).getBoundingClientRect().width;
        sampledWeightWidthsArray.push(sampledWeightWidth);
        sampledColumnWidthsRef.current.weightWidths[columnsArray[i].key] = sampledWeightWidth;
      }
      // re-enable wrapping, allowing measurements to represent minimum auto-resizable column widths
      outerTableElement.classList.remove(tableStyles.disableWrapping);
      for (let i = 0; i < headerElements.length; i++) {
        const sampledMinWidth = (headerElements[i] as HTMLElement).getBoundingClientRect().width;
        sampledMinWidthsArray.push(sampledMinWidth);
        sampledColumnWidthsRef.current.minWidths[columnsArray[i].key] = sampledMinWidth;
      }
    }

    for (let i = 0; i < sampledMinWidthsArray.length; i++) {
      const forcedWidth = forcedColumnWidthsArray[i];
      if (forcedWidth == null) {
        let columnWidth;
        const sampledMinWidth = sampledMinWidthsArray[i];
        const minWidth = columnMinWidthsArray[i];
        const maxWidth = columnMaxWidthsArray[i];
        if (minWidth != null && minWidth >= sampledMinWidth) {
          columnWidth = minWidth;
        } else if (maxWidth != null && sampledMinWidth >= maxWidth) {
          columnWidth = maxWidth;
        } else {
          columnWidth = sampledMinWidth;
        }
        pendingTotalWidth += columnWidth;
        // replace sampled weight with min width if needed
        const columnWeight =
          minWidth != null && minWidth >= sampledWeightWidthsArray[i]
            ? minWidth
            : sampledWeightWidthsArray[i];
        sampledWeightWidthsArray[i] = columnWeight;
        totalPreferredWidth +=
          maxWidth != null && maxWidth <= columnWeight ? maxWidth : columnWeight;
        totalWorkingWeight += columnWeight;
        totalRemainingWeight += columnWeight - columnWidth;
        // newColumnWidthsArray already has content, so update in place rather than 'push'
        newColumnWidthsArray[i] = columnWidth;
        appliedColumnWidthsArray.push(columnWidth);
      } else {
        forcedTotalWidth += forcedWidth;
        pendingTotalWidth += forcedWidth;
        totalPreferredWidth += forcedWidth;
        appliedColumnWidthsArray.push(forcedWidth);
      }
    }
    // special case for initial rendering where we don't know if overall width is constrained
    if (availableWidth === -1) {
      availableWidth = totalPreferredWidth;
    }
    if (pendingTotalWidth < availableWidth) {
      _enforceContentsMinMaxRules(
        columnsArray,
        availableWidth,
        totalPreferredWidth,
        totalRemainingWeight,
        totalWorkingWeight,
        pendingTotalWidth,
        forcedTotalWidth,
        forcedColumnWidthsArray,
        sampledWeightWidthsArray,
        columnMaxWidthsArray,
        newColumnWidthsArray,
        appliedColumnWidthsArray
      );
    }
  }
  return _createColumnWidths(columnsArray, newColumnWidthsArray);
}

/**
 * Helper function to enforce all of the layout sizing rules on the Table's columns.
 */
function _enforceContentsMinMaxRules<K, D, C>(
  columnsArray: TableColumn<K, D, C>[],
  availableWidth: number,
  totalPreferredWidth: number,
  totalRemainingWeight: number,
  totalWorkingWeight: number,
  appliedPendingTotalWidth: number,
  forcedTotalWidth: number,
  forcedColumnWidthsArray: (number | undefined)[],
  columnWeightsArray: number[],
  columnMaxWidthsArray: (number | undefined)[],
  newColumnWidthsArray: number[],
  appliedColumnWidthsArray: number[]
) {
  // try and honor the weights of each column combined with the min widths, and then check that against
  // any max widths once completed. if the final result doesn't fill the space AND at least 1 max width
  // rule was enforced, repeat the process until no max rules are enforced OR the space is filled.
  let isMaxRuleApplied = true;
  while (isMaxRuleApplied) {
    isMaxRuleApplied = false;
    if (totalPreferredWidth > availableWidth) {
      _enforceConstrainedWeightRules(
        columnsArray,
        availableWidth - appliedPendingTotalWidth,
        totalRemainingWeight,
        columnWeightsArray,
        forcedColumnWidthsArray,
        newColumnWidthsArray,
        appliedColumnWidthsArray
      );
    } else {
      _enforceWeightedMinRules(
        columnsArray,
        availableWidth,
        totalWorkingWeight,
        forcedTotalWidth,
        forcedColumnWidthsArray,
        columnWeightsArray,
        newColumnWidthsArray,
        appliedColumnWidthsArray
      );
    }

    let pendingTotalWidth = 0;
    for (let i = 0; i < columnsArray.length; i++) {
      if (forcedColumnWidthsArray[i] == null) {
        const maxWidth = columnMaxWidthsArray[i];
        if (maxWidth != null && newColumnWidthsArray[i] > maxWidth) {
          isMaxRuleApplied = true;
          forcedColumnWidthsArray[i] = maxWidth;
          forcedTotalWidth += maxWidth;
          appliedPendingTotalWidth += maxWidth - appliedColumnWidthsArray[i];
          totalWorkingWeight -= columnWeightsArray[i];
          newColumnWidthsArray[i] = maxWidth;
          appliedColumnWidthsArray[i] = maxWidth;
        }
      }
      pendingTotalWidth += newColumnWidthsArray[i];
    }
    isMaxRuleApplied = isMaxRuleApplied && pendingTotalWidth < availableWidth;
  }
}

/**
 * Helper function to enforce constrained weights / min width rules on the Table's columns.
 */
function _enforceConstrainedWeightRules<K, D, C>(
  columnsArray: TableColumn<K, D, C>[],
  initialExtraWidth: number,
  totalRemainingWeight: number,
  columnWeightsArray: number[],
  forcedColumnWidthsArray: (number | undefined)[],
  newColumnWidthsArray: number[],
  appliedColumnWidthsArray: number[]
) {
  if (initialExtraWidth > 0) {
    let availableWidth = initialExtraWidth;
    let currentWeightTotal = totalRemainingWeight;
    for (let i = 0; i < columnsArray.length; i++) {
      if (currentWeightTotal > 0 && forcedColumnWidthsArray[i] == null) {
        const currentWeight = columnWeightsArray[i] - appliedColumnWidthsArray[i];
        const weightWidth = (currentWeight / currentWeightTotal) * availableWidth;
        newColumnWidthsArray[i] = weightWidth + appliedColumnWidthsArray[i];
        availableWidth -= weightWidth;
        currentWeightTotal -= currentWeight;
      }
    }
  }
}

/**
 * Helper function to enforce the weights / min width rules on the Table's columns.
 */
function _enforceWeightedMinRules<K, D, C>(
  columnsArray: TableColumn<K, D, C>[],
  availableWidth: number,
  totalWeight: number,
  forcedTotalWidth: number,
  forcedColumnWidthsArray: (number | undefined)[],
  columnWeightsArray: number[],
  newColumnWidthsArray: number[],
  appliedColumnWidthsArray: number[]
) {
  const pendingForcedColumnWidths = [] as (number | undefined)[];
  let isMinRuleApplied = true;
  while (isMinRuleApplied) {
    isMinRuleApplied = false;
    let currentWeightTotal = totalWeight;
    let widthDiff = availableWidth - forcedTotalWidth;
    if (widthDiff > 0) {
      for (let i = 0; i < columnsArray.length; i++) {
        if (
          currentWeightTotal > 0 &&
          forcedColumnWidthsArray[i] == null &&
          pendingForcedColumnWidths[i] == null
        ) {
          const currentWeight = columnWeightsArray[i];
          const newWidth = (currentWeight / currentWeightTotal) * widthDiff;

          // if calculated width breaks min width requirement, force min width as needed
          const minWidth = appliedColumnWidthsArray[i];
          if (newWidth < minWidth) {
            // the preferred weighted size conflicts with the min size, so set pending to min size
            pendingForcedColumnWidths[i] = minWidth;
            forcedTotalWidth += minWidth;
            newColumnWidthsArray[i] = minWidth;
            isMinRuleApplied = true;
            totalWeight -= currentWeight;
            break;
          }
          newColumnWidthsArray[i] = newWidth;
          widthDiff -= newWidth;
          currentWeightTotal -= currentWeight;
        }
      }
    }
  }
}

/**
 * Helper function to create a ColumnWidths object from a TableColumn array and a widths array.
 */
function _createColumnWidths<K, D, C extends string>(
  columnsArray: TableColumn<K, D, C>[],
  columnWidthsArray: number[]
) {
  const columnWidths = {} as ColumnWidths<C>;
  for (let i = 0; i < columnsArray.length; i++) {
    columnWidths[columnsArray[i].key] = columnWidthsArray[i];
  }
  return columnWidths;
}

/**
 * Helper method to apply the given sizing object to the currently rendered Table
 * and column elements.
 */
function _udpateTableSizing(
  root: HTMLDivElement,
  tableId: string,
  widthsArray: number[],
  totalWidth: number
) {
  const tableCols = root.querySelectorAll(`[data-oj-table-col='${tableId}']`);
  for (let i = 0; i < tableCols.length; i++) {
    const tableCol = tableCols[i] as HTMLTableColElement;
    tableCol.style.width = `${widthsArray[i]}px`;
  }
  const innerTableElem = root.querySelectorAll(
    `[data-oj-table-inner-table='${tableId}']`
  )[0] as HTMLElement;
  innerTableElem.style.width = `${totalWidth}px`;
  innerTableElem.style.tableLayout = 'fixed';
}
